import { resolveOrganizationAlias } from "./organizationAliases";
import { normalizeRouteSegment } from "./reservedRoutes";
import { getCurrentHostname, isLightlyPlatformHost } from "./routing/hostUtils";
import { getOrgCustomDomain } from "../config/customDomainMappings";

// Stable HTTPS origin for organizations hosted on the shared platform (no custom
// domain). Prefers a configured public base URL; never window.location.origin,
// localhost, dev, or preview hosts. Forced to HTTPS.
const DEFAULT_PLATFORM_PUBLIC_ORIGIN = "https://lightly.web.app";

function toHttpsOrigin(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withScheme = /^https?:\/\//i.test(raw)
    ? raw.replace(/^http:\/\//i, "https://")
    : `https://${raw}`;
  try {
    return new URL(withScheme).origin;
  } catch {
    return "";
  }
}

export const RCCG_ORG_SLUG =
  "the-redeemed-christian-church-of-god-chapel-of-mercy-00405731";
export const FAITH_SANCTUARY_CAMPAIGN_SLUG = "faith-sanctuary";
export const BUILDING_FUND_DONATION_ROUTE_SLUG = "building-fund-general";

const LEGACY_ROUTE_ALIASES = {
  [FAITH_SANCTUARY_CAMPAIGN_SLUG]: {
    routeType: "campaignMicrosite",
    orgSlug: RCCG_ORG_SLUG,
    campaignSlug: FAITH_SANCTUARY_CAMPAIGN_SLUG,
  },
  "rccg-chapel-of-mercy": {
    routeType: "organizationHome",
    orgSlug: RCCG_ORG_SLUG,
  },
  "church-fundraising-rccg.web.app": {
    routeType: "organizationHome",
    orgSlug: RCCG_ORG_SLUG,
  },
};

const ORGANIZATION_PAGE_KEYS = new Set([
  "about",
  "new-here",
  "beliefs",
  "ministries",
  "events",
  "media",
  "messages",
  "series",
  "gallery",
  "leadership",
  "bible",
  "attendance",
  "checkin",
  "kiosk",
  "giving",
  "give",
  "contact",
  "prayer-request",
  "privacy",
  "refund-policy",
  "terms",
  "services",
  "updates",
  "flyers",
]);

function cleanSegment(value) {
  return String(value || "").trim().replace(/^\/+|\/+$/g, "");
}

function canonicalOrgSlug(value) {
  const cleaned = cleanSegment(value);
  return resolveOrganizationAlias(cleaned) || cleaned;
}

function encodePathSegment(value) {
  return encodeURIComponent(cleanSegment(value));
}

function shouldPreserveLink(value) {
  const raw = String(value || "").trim();
  return (
    /^https?:\/\//i.test(raw) ||
    /^mailto:/i.test(raw) ||
    /^tel:/i.test(raw) ||
    raw.startsWith("#")
  );
}

function isCustomDomainContext(options = {}) {
  if (typeof options.isCustomDomain === "boolean") return options.isCustomDomain;
  const hostname = options.hostname || getCurrentHostname();
  return !isLightlyPlatformHost(hostname);
}

function normalizePublicPath(path = "") {
  const raw = String(path || "").trim();
  if (!raw) return "/";
  if (shouldPreserveLink(raw)) return raw;

  const match = raw.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);
  const pathPart = (match?.[1] || "")
    .replace(/\\/g, "/")
    .split("/")
    .map(cleanSegment)
    .filter(Boolean)
    .join("/");
  const suffix = `${match?.[2] || ""}${match?.[3] || ""}`;

  return pathPart ? `/${pathPart}${suffix}` : `/${suffix}`;
}

export function buildOrganizationHomePath(orgSlug) {
  const slug = canonicalOrgSlug(orgSlug);
  return slug ? `/${encodePathSegment(slug)}` : "/";
}

export function getOrgHomePath(orgSlug, options = {}) {
  if (isCustomDomainContext(options)) return "/";
  return buildOrganizationHomePath(orgSlug);
}

export function buildPublicOrgPath(orgSlug, path = "", options = {}) {
  const cleanPath = normalizePublicPath(path);
  if (shouldPreserveLink(cleanPath)) return cleanPath;
  if (isCustomDomainContext(options)) return cleanPath;
  const homePath = buildOrganizationHomePath(orgSlug);
  if (homePath === "/") return cleanPath;
  return cleanPath === "/" ? homePath : `${homePath}${cleanPath}`;
}

export function buildOrgPath(orgSlug, path = "", options = {}) {
  return buildPublicOrgPath(orgSlug, path, options);
}

function resolveOrgCustomDomain(orgIdentifier) {
  return (
    getOrgCustomDomain(orgIdentifier) ||
    getOrgCustomDomain(canonicalOrgSlug(orgIdentifier))
  );
}

// Platform public origin used when an organization has no custom domain.
export function getPlatformPublicOrigin() {
  return (
    toHttpsOrigin(process.env.REACT_APP_PUBLIC_BASE_URL) ||
    DEFAULT_PLATFORM_PUBLIC_ORIGIN
  );
}

// Canonical public ORIGIN for an organization, independent of the current
// browser host:
//   - verified custom domain -> https://that-domain   (e.g. https://rccgcm.org)
//   - otherwise              -> the platform public origin
// Works for every organization/business template (church, nonprofit, school,
// childcare, cultural, community, business, future verticals): the only input
// is the org identity and its domain mapping.
export function getCanonicalPublicOrigin(orgIdentifier) {
  const customDomain = resolveOrgCustomDomain(orgIdentifier);
  if (customDomain) return `https://${customDomain}`;
  return getPlatformPublicOrigin();
}

// Build an absolute, canonical, HTTPS public URL for an organization route.
// `path` must be slug-LESS (e.g. "/checkin?serviceId=..."). On a custom domain
// the internal org slug is omitted from the path; on the platform host the slug
// prefix is kept. Query string and hash are preserved exactly.
export function buildCanonicalPublicUrl(orgIdentifier, path = "", options = {}) {
  const origin = getCanonicalPublicOrigin(orgIdentifier);
  const isCustomDomain = Boolean(resolveOrgCustomDomain(orgIdentifier));
  const relativePath = buildPublicOrgPath(orgIdentifier, path, {
    ...options,
    isCustomDomain,
  });
  return `${origin}${relativePath}`;
}

export function buildOrgRoute(orgSlug, path = "", options = {}) {
  return buildOrgPath(orgSlug, path, options);
}

export function buildOrgAppRoute(orgSlug, path = "", options = {}) {
  const cleanPath = normalizePublicPath(path);
  if (shouldPreserveLink(cleanPath)) return cleanPath;
  const appPath = cleanPath === "/" ? "/app" : cleanPath.startsWith("/app/") ? cleanPath : `/app${cleanPath}`;
  return buildOrgPath(orgSlug, appPath, options);
}

export function resolveOrgAwareNavigation({
  orgSlug = "",
  path = "",
  fallback = "/",
  isAppRoute = false,
  isCustomDomain,
  hostname,
} = {}) {
  const cleanPath = normalizePublicPath(path || fallback);
  if (shouldPreserveLink(cleanPath)) return cleanPath;
  const options = { isCustomDomain, hostname };
  if (isAppRoute) return buildOrgAppRoute(orgSlug, cleanPath, options);
  return buildOrgRoute(orgSlug, cleanPath, options);
}

export function buildCampaignPath(orgSlug, campaignSlug, options = {}) {
  const campaign = cleanSegment(campaignSlug);
  return campaign ? buildPublicOrgPath(orgSlug, `/campaigns/${encodePathSegment(campaign)}`, options) : getOrgHomePath(orgSlug, options);
}

export function getCleanCustomDomainOrgSlugPath({
  pathname = "/",
  search = "",
  hash = "",
  orgSlug = "",
} = {}) {
  const slug = canonicalOrgSlug(orgSlug);
  if (!slug) return "";

  const rawPath = String(pathname || "/").split("?")[0].split("#")[0] || "/";
  const segments = rawPath.split("/").filter(Boolean);
  const prefixLength = segments[0] === "o" ? 2 : 1;
  let routeSlug;
  try { routeSlug = canonicalOrgSlug(decodeURIComponent(segments[prefixLength - 1] || "")); } catch { return ""; }
  if (routeSlug !== slug) return "";

  const cleanPath = segments.slice(prefixLength).length
    ? `/${segments.slice(prefixLength).join("/")}`
    : "/";
  return `${cleanPath}${search || ""}${hash || ""}`;
}

export function getPublicParentRoute(input = {}) {
  const orgSlug = canonicalOrgSlug(input.orgSlug);
  const homePath = buildOrganizationHomePath(orgSlug);
  const routeType = String(input.routeType || "");
  const campaignSlug = cleanSegment(input.campaignSlug || input.campaignId);
  const participantSlug = cleanSegment(input.participantSlug || input.slug);
  const pageKey = cleanSegment(input.pageKey);
  const donationRouteSlug = cleanSegment(input.donationRouteSlug);

  if (!orgSlug) return "/";

  if (input.parentHref) return input.parentHref;

  if (routeType === "organizationPage" || pageKey) {
    return homePath;
  }

  if (routeType === "campaignMicrosite") {
    return homePath;
  }

  if (routeType === "campaignFundraiserSignup") {
    return buildCampaignPath(orgSlug, campaignSlug);
  }

  if (routeType === "campaignParticipantPage") {
    return buildCampaignPath(orgSlug, campaignSlug);
  }

  if (routeType === "campaignParticipantDonation") {
    return participantSlug
      ? `${buildCampaignPath(orgSlug, campaignSlug)}/f/${encodePathSegment(participantSlug)}`
      : buildCampaignPath(orgSlug, campaignSlug);
  }

  if (routeType === "eventDetail") {
    return `${homePath}/events`;
  }

  if (routeType === "ministryDetail") {
    return `${homePath}/ministries`;
  }

  if (routeType === "donationRoute") {
    if (campaignSlug) return buildCampaignPath(orgSlug, campaignSlug);
    if (donationRouteSlug === BUILDING_FUND_DONATION_ROUTE_SLUG) {
      return buildCampaignPath(orgSlug, FAITH_SANCTUARY_CAMPAIGN_SLUG);
    }
    return homePath;
  }

  return homePath;
}

export function buildCanonicalPublicPath(route = {}) {
  const orgPath = buildOrganizationHomePath(route.orgSlug);
  if (!route.orgSlug) return "/";

  switch (route.routeType) {
    case "organizationHome":
      return orgPath;
    case "organizationPage":
      return route.pageKey ? `${orgPath}/${encodePathSegment(route.pageKey)}` : orgPath;
    case "campaignMicrosite":
      return `${orgPath}/campaigns/${encodePathSegment(route.campaignSlug)}`;
    case "campaignFundraiserSignup":
      return `${orgPath}/campaigns/${encodePathSegment(route.campaignSlug)}/become-a-fundraiser`;
    case "campaignParticipantPage":
      return `${orgPath}/campaigns/${encodePathSegment(route.campaignSlug)}/f/${encodePathSegment(route.participantSlug)}`;
    case "campaignParticipantDonation":
      return `${orgPath}/campaigns/${encodePathSegment(route.campaignSlug)}/f/${encodePathSegment(route.participantSlug)}/donate`;
    case "donationRoute":
      return `${orgPath}/donate/${encodePathSegment(route.donationRouteSlug)}`;
    default:
      return orgPath;
  }
}

function resolveCanonicalPublicRouteRaw(input = {}) {
  const pathname = cleanSegment(input.pathname || "");
  const params = input.params || {};
  const segments = pathname ? pathname.split("/").map(cleanSegment).filter(Boolean) : [];
  // Custom domains supply organization identity through context instead of a
  // path segment. Normalize to the same shape as shared-host public routes.
  if (segments[0] === "o" && segments[1]) segments.shift();
  if (params.orgSlug && ["campaigns", "c", "donate"].includes(segments[0])) {
    segments.unshift(canonicalOrgSlug(params.orgSlug));
  }
  const first = normalizeRouteSegment(segments[0] || params.orgSlug || params.slug || "");
  const orgSlug = canonicalOrgSlug(params.orgSlug || segments[0] || "");

  if (!segments.length && !params.orgSlug) {
    return { routeType: "organizationHome", orgSlug: "" };
  }

  if (segments[0] === "donate" && (params.slug || segments[1])) {
    const donationRouteSlug = params.slug || segments[1];
    if (donationRouteSlug === BUILDING_FUND_DONATION_ROUTE_SLUG) {
      return {
        routeType: "donationRoute",
        orgSlug: RCCG_ORG_SLUG,
        campaignSlug: FAITH_SANCTUARY_CAMPAIGN_SLUG,
        donationRouteSlug,
      };
    }
  }

  const legacyAlias = LEGACY_ROUTE_ALIASES[first];
  if (legacyAlias) {
    if (segments[1] === "become-a-fundraiser") {
      return { ...legacyAlias, routeType: "campaignFundraiserSignup" };
    }
    if (segments[1] === "f" && (params.slug || segments[2])) {
      return {
        ...legacyAlias,
        routeType: segments[3] === "donate" ? "campaignParticipantDonation" : "campaignParticipantPage",
        participantSlug: params.slug || segments[2],
      };
    }
    return legacyAlias;
  }

  if (segments[1] === "donate" && (params.donationRouteSlug || segments[2])) {
    const donationRouteSlug = params.donationRouteSlug || segments[2];
    return {
      routeType: "donationRoute",
      orgSlug,
      donationRouteSlug,
      campaignSlug:
        donationRouteSlug === BUILDING_FUND_DONATION_ROUTE_SLUG
          ? FAITH_SANCTUARY_CAMPAIGN_SLUG
          : params.campaignSlug || params.campaignId,
    };
  }

  if (segments[1] === "campaigns" && (params.campaignId || params.campaignSlug || segments[2])) {
    const campaignSlug = params.campaignId || params.campaignSlug || segments[2];
    if (segments[3] === "become-a-fundraiser") {
      return { routeType: "campaignFundraiserSignup", orgSlug, campaignSlug };
    }
    if (["f", "fundraisers"].includes(segments[3]) && (params.slug || params.participantSlug || segments[4])) {
      return {
        routeType: segments[5] === "donate" ? "campaignParticipantDonation" : "campaignParticipantPage",
        orgSlug,
        campaignSlug,
        participantSlug: params.slug || params.participantSlug || segments[4],
      };
    }
    return { routeType: "campaignMicrosite", orgSlug, campaignSlug };
  }

  if (segments[1] === "c" && (params.campaignId || segments[2])) {
    const campaignSlug = params.campaignId || segments[2];
    if (segments[3] === "become-a-fundraiser") {
      return { routeType: "campaignFundraiserSignup", orgSlug, campaignSlug };
    }
    if (segments[3] === "donate") {
      return { routeType: "campaignMicrosite", orgSlug, campaignSlug };
    }
    return { routeType: "campaignMicrosite", orgSlug, campaignSlug };
  }

  if (segments[1] && ORGANIZATION_PAGE_KEYS.has(segments[1])) {
    if (segments[1] === "events" && (params.eventSlug || params.eventId || segments[2])) {
      return {
        routeType: "eventDetail",
        orgSlug,
        pageKey: "events",
        eventSlug: params.eventSlug || params.eventId || segments[2],
      };
    }
    if (segments[1] === "ministries" && (params.ministrySlug || segments[2])) {
      return {
        routeType: "ministryDetail",
        orgSlug,
        pageKey: "ministries",
        ministrySlug: params.ministrySlug || segments[2],
      };
    }
    return { routeType: "organizationPage", orgSlug, pageKey: segments[1] };
  }

  return { routeType: "organizationHome", orgSlug };
}

export function withCanonicalPath(route = {}) {
  const canonicalPath = buildCanonicalPublicPath(route);
  return { ...route, canonicalPath };
}

export function resolveCanonicalPublicRoute(input = {}) {
  return withCanonicalPath(resolveCanonicalPublicRouteRaw(input));
}
